import { Button, Collapse, Input, Modal } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { classNames } from 'src/utilities';
import {
  usePushEhrCredentialsV2,
  useGetEhrCredentialsV2,
  useDeleteEhrCredentialsV2,
  useOnboardEhr,
} from 'src/hooks/ehr';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';

const OP_EHR_SYSTEMS = ['office_practicum', 'office_practicum_push'];
// Service account used to kick off EHR onboarding on behalf of the practice.
const ONBOARDING_USER_EMAIL = 'api@insighthealth.ai';

// Examples of what OP sends:
//   Token URL: https://security.op.healthcare/2/auth/realms/4-OP-Realm/protocol/openid-connect/token
//   Client ID: 4-ih-ai-scribe-fhir
// From those we can extract practice_id ("4") and api_version ("2").
const extractPracticeId = (clientId, tokenUrl) => {
  const fromClient = clientId?.match(/^(\d+)-/)?.[1];
  if (fromClient) return fromClient;
  return tokenUrl?.match(/\/realms\/(\d+)-/)?.[1] || '';
};
const extractApiVersion = (tokenUrl) => {
  const match = tokenUrl?.match(/security\.op\.healthcare\/(\d+)\//);
  return match ? Number(match[1]) : undefined;
};

const OfficePracticumPage = ({ orgUuid: orgUuidProp } = {}) => {
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');

  const { pushCredentials, isPending: isSaving } = usePushEhrCredentialsV2();
  const { onboardEhr, isPending: isOnboarding } = useOnboardEhr();
  const { deleteCredentials, isPending: isDeleting } =
    useDeleteEhrCredentialsV2();
  const isPending = isSaving || isOnboarding;

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const orgUuid = orgUuidProp || currentPractitioner?.org_uuid;

  // GET /v2/ehr/credentials — backend returns client_secret masked
  // (e.g. "••••••••cW5G"). We display whatever it sends without unmasking.
  const {
    data: credsResponse,
    isFetching: isLoadingCreds,
    refetch: refetchCreds,
  } = useGetEhrCredentialsV2(
    { org_uuid: orgUuid, ehr_system: 'office_practicum' },
    { enabled: Boolean(orgUuid) }
  );
  const savedCreds = credsResponse?.data || null;
  const isSaved = Boolean(savedCreds?.client_id);

  const practiceId = useMemo(
    () => extractPracticeId(clientId, tokenUrl),
    [clientId, tokenUrl]
  );
  const apiVersion = useMemo(() => extractApiVersion(tokenUrl), [tokenUrl]);

  useEffect(() => {
    if (savedCreds?.client_id) {
      setClientId(savedCreds.client_id || '');
      setClientSecret(savedCreds.client_secret || '');
      // Reconstruct a token URL so the saved view still shows something
      // meaningful. Display only; never sent back.
      if (savedCreds.practice_id && savedCreds.api_version) {
        setTokenUrl(
          `https://security.op.healthcare/${savedCreds.api_version}/auth/realms/${savedCreds.practice_id}-OP-Realm/protocol/openid-connect/token`
        );
      } else {
        setTokenUrl('');
      }
    } else {
      setClientId('');
      setClientSecret('');
      setTokenUrl('');
    }
  }, [savedCreds]);

  const handleSubmit = async () => {
    if (!orgUuid) return;
    if (!clientId.trim() || !clientSecret.trim() || !tokenUrl.trim()) {
      showToastError('Please fill in all required fields');
      return;
    }
    if (!practiceId || !apiVersion) {
      showToastError(
        'Could not read Practice ID / API Version from the provided values. Check the Client ID and Token URL.'
      );
      return;
    }

    try {
      const credentials = {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        practice_id: practiceId,
        api_version: apiVersion,
      };
      await Promise.all(
        OP_EHR_SYSTEMS.map((ehr_system) =>
          pushCredentials({ org_uuid: orgUuid, ehr_system, credentials })
        )
      );
      showToastSuccess('Office Practicum credentials saved successfully');

      try {
        await onboardEhr({
          org_uuid: orgUuid,
          ehr_system: 'office_practicum',
          practice_id: practiceId,
          user_email: ONBOARDING_USER_EMAIL,
          timezone: 'UTC',
        });
        showToastSuccess('Organization onboarded to Office Practicum');
      } catch (onboardError) {
        const msg =
          onboardError?.response?.data?.message ||
          onboardError?.message ||
          'Credentials saved, but onboarding failed. Please retry.';
        showToastError(msg);
      }

      refetchCreds();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save credentials';
      showToastError(msg);
    }
  };

  const handleDelete = async () => {
    if (!orgUuid) return;
    try {
      await Promise.all(
        OP_EHR_SYSTEMS.map((ehr_system) =>
          deleteCredentials({ org_uuid: orgUuid, ehr_system })
        )
      );
      showToastSuccess('Office Practicum credentials deleted successfully');
      setClientId('');
      setClientSecret('');
      setTokenUrl('');
      setDeleteModalVisible(false);
      setDeleteConfirmText('');
      refetchCreds();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete credentials';
      showToastError(msg);
    }
  };

  const inputClass = (disabled) =>
    classNames(
      'font-sans w-full p-2 border rounded',
      'text-input bg-gray-25 hover:bg-gray-50 transition-colors duration-200',
      'focus:outline-none focus:ring-1 focus:ring-gray-200 focus:border-gray-200',
      disabled ? 'cursor-not-allowed bg-gray-100' : ''
    );

  return (
    <div className="p-4 sm:p-6">
      <div className="text-lg font-bold font-sans mb-4">
        Setup API Credentials for Office Practicum
      </div>

      <Collapse
        className="mb-4 bg-blue-50"
        items={[
          {
            key: 'how-to',
            label: (
              <span className="font-semibold">
                How to get your Office Practicum credentials
              </span>
            ),
            children: (
              <div className="text-sm text-gray-700 flex flex-col gap-3">
                <div>
                  Office Practicum provisions FHIR API credentials per practice.
                  Email <strong>support@officepracticum.com</strong> (cc your
                  Account Manager) and request{' '}
                  <em>
                    FHIR R4 API access for a third-party vendor (Insight Health)
                  </em>
                  . OP will send back a packet that looks like this:
                </div>
                <pre className="bg-white border rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                  {`Practice       : Babar's Better Pediatrics
API Base URL   : https://applications.op.healthcare/4/api/FHIR/R4/
Token URL      : https://security.op.healthcare/2/auth/realms/4-OP-Realm/protocol/openid-connect/token
Client ID      : 4-ih-ai-scribe-fhir
Client Secret  : 4oBKSo9o4EfrSwDGg3PrtRaXSvp9cW5G
Scopes         : system/Patient.read, system/Appointment.read, ...`}
                </pre>
                <div>
                  Paste the <strong>Client ID</strong>,{' '}
                  <strong>Client Secret</strong>, and <strong>Token URL</strong>{' '}
                  below exactly as OP sent them. Everything else is derived
                  automatically.
                </div>
                <div className="text-xs text-gray-500">
                  Ask OP to include the full Insight Health scope list (Patient,
                  Appointment, Encounter, Observation, MedicationRequest,
                  DocumentReference read/write, offline_access, etc.) so all
                  features work end-to-end.
                </div>
              </div>
            ),
          },
        ]}
      />

      {isSaved && (
        <div className="mb-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
          Credentials are configured for this organization. The Client Secret is
          masked for security.
        </div>
      )}

      <div className="flex flex-col gap-4">
        <input
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="Client ID (e.g. 4-ih-ai-scribe-fhir)"
          readOnly={isSaved}
          className={inputClass(isSaved)}
        />
        <input
          type={isSaved ? 'text' : 'password'}
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder="Client Secret"
          readOnly={isSaved}
          className={inputClass(isSaved)}
        />
        <input
          type="text"
          value={tokenUrl}
          onChange={(e) => setTokenUrl(e.target.value)}
          placeholder="Token URL (e.g. https://security.op.healthcare/2/auth/realms/4-OP-Realm/...)"
          readOnly={isSaved}
          className={inputClass(isSaved)}
        />

        {!isSaved && (practiceId || apiVersion) && (
          <div className="text-xs text-gray-500">
            Detected: Practice ID <code>{practiceId || '—'}</code>, API Version{' '}
            <code>{apiVersion ?? '—'}</code>
          </div>
        )}

        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isPending || isLoadingCreds}
          disabled={isSaved}
          className={classNames(
            'w-full',
            isSaved ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          Submit
        </Button>

        {isSaved && (
          <Button
            danger
            onClick={() => setDeleteModalVisible(true)}
            loading={isDeleting}
            className="w-full !bg-red-600 !hover:bg-red-700 !text-white"
            style={{ borderColor: '#dc2626' }}
          >
            Delete Credentials
          </Button>
        )}
      </div>

      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteConfirmText('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setDeleteModalVisible(false);
              setDeleteConfirmText('');
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={isDeleting}
            disabled={deleteConfirmText !== 'delete' || isDeleting}
            onClick={handleDelete}
          >
            Delete
          </Button>,
        ]}
      >
        <div className="py-4">
          <p className="mb-4">
            This will remove the saved Office Practicum credentials for this
            organization. This action cannot be undone. To confirm, please type{' '}
            <strong>delete</strong> below:
          </p>
          <Input
            placeholder="Type 'delete' to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            className="w-full"
          />
        </div>
      </Modal>
    </div>
  );
};

export default OfficePracticumPage;
